import * as THREE from 'three';
import { ft } from '../core/units.js';
import { LINENS, OBJECTS, SEAT_RADIUS_FEET } from '../data/objects.js';
import { banquetChair, merged, roundTable } from './models.js';

/**
 * Draws whatever `setup.js` says is standing in the ballroom.
 *
 * Pooled, not rebuilt. A full house is six rounds, forty-eight chairs and six
 * linens, and the chairs re-space themselves every time one is added — so the
 * naive version disposes and recreates fifty-odd meshes on every single button
 * press. Instead the pool only ever grows, and everything past the current
 * count is hidden.
 *
 * One deviation from "boxes and planes": a 72 inch round is drawn as a low-poly
 * cylinder. It is still an untextured primitive in a flat colour, and the
 * alternative — a square round — would misrepresent the exact geometry the
 * grading and the seating maths are working from. The round stands on its
 * folding legs and the chairs are banquet chairs (`models.js`), the same ones
 * that stand loose and stacked everywhere else; linens are discs.
 *
 * The anchor rings are the room's own grid, which is a real thing a ballroom
 * floor has. They are *not* a hint: nothing here knows which anchors the BEO
 * called for, so the diagram on the board remains the only place that is
 * written down.
 */

const ANCHOR_COLOUR = 0x8a7f70;
const GHOST_COLOUR = 0xd2b14a;
const GHOST_BAD_COLOUR = 0xe0685f;

/** Metres from the table centre to a chair. Table radius plus a working gap. */
export const SEAT_RADIUS = ft(SEAT_RADIUS_FEET);

export function createSetupView({ setup }) {
  const group = new THREE.Group();
  group.name = 'ballroom-setup';

  const disposables = [];
  const keep = (thing) => {
    disposables.push(thing);
    return thing;
  };

  // --- shared geometry ------------------------------------------------------
  const tableGeometry = keep(
    new THREE.CylinderGeometry(ft(OBJECTS.round.radius), ft(OBJECTS.round.radius), ft(0.25), 20)
  );
  const legGeometry = keep(merged({ legs: roundTable().legs }, ['legs']));
  const linenGeometry = keep(
    new THREE.CylinderGeometry(ft(OBJECTS.round.radius + 0.9), ft(OBJECTS.round.radius + 0.4), ft(1.4), 20, 1, true)
  );
  const chairGeometry = keep(merged(banquetChair(), ['frame', 'cushion']));
  const ringGeometry = keep(new THREE.RingGeometry(ft(2.6), ft(3), 24));

  const tableMaterial = keep(
    new THREE.MeshStandardMaterial({ color: OBJECTS.round.colour, roughness: 0.9 })
  );
  const legMaterial = keep(new THREE.MeshStandardMaterial({ color: 0x3a3a3c, metalness: 0.5, roughness: 0.5 }));
  const chairFrameMaterial = keep(new THREE.MeshStandardMaterial({ color: 0xb8963e, metalness: 0.7, roughness: 0.35 }));
  const chairMaterial = keep(
    new THREE.MeshStandardMaterial({ color: OBJECTS.chair.colour, roughness: 0.92 })
  );
  const linenMaterials = new Map(
    Object.values(LINENS).map((linen) => [
      linen.id,
      keep(
        new THREE.MeshStandardMaterial({
          color: linen.colour,
          roughness: 0.98,
          side: THREE.DoubleSide,
        })
      ),
    ])
  );
  const anchorMaterial = keep(
    new THREE.MeshBasicMaterial({
      color: ANCHOR_COLOUR,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
  );
  const ghostMaterial = keep(
    new THREE.MeshBasicMaterial({
      color: GHOST_COLOUR,
      transparent: true,
      opacity: 0.75,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
  );

  // --- the room's own grid --------------------------------------------------
  const anchorGroup = new THREE.Group();
  anchorGroup.visible = false;
  for (const anchor of setup.anchors) {
    const ring = new THREE.Mesh(ringGeometry, anchorMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(anchor.x, 0.012, anchor.z);
    anchorGroup.add(ring);
  }
  group.add(anchorGroup);

  const ghost = new THREE.Mesh(ringGeometry, ghostMaterial);
  ghost.rotation.x = -Math.PI / 2;
  ghost.position.y = 0.02;
  ghost.visible = false;
  group.add(ghost);

  // --- what a dressed table has on it ----------------------------------------
  /*
   * Once a round has its linen it is a table at a function, and it looks like
   * one: a cloth top in the same linen as the skirt, a low centrepiece of
   * flowers with three candles, and at every chair a gilt charger, a plate, a
   * folded napkin and two glasses. All of it drawn from a handful of instanced
   * meshes, and all of it decoration: the linen's colour and the number of
   * chairs, which are what the grading reads, are exactly as they were.
   */
  const MAX_SETTINGS = 96;
  const MAX_CENTRES = 16;
  const dummy = new THREE.Object3D();
  const topY = ft(OBJECTS.round.height) + ft(0.125);
  const instanced = (geometry, material, count) => {
    const mesh = new THREE.InstancedMesh(keep(geometry), keep(material), count);
    mesh.count = 0;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    group.add(mesh);
    return mesh;
  };
  const dressing = {
    charger: instanced(new THREE.CylinderGeometry(ft(0.55), ft(0.55), 0.008, 24), new THREE.MeshStandardMaterial({ color: 0xc9a35b, metalness: 0.8, roughness: 0.3 }), MAX_SETTINGS),
    plate: instanced(new THREE.CylinderGeometry(ft(0.42), ft(0.36), 0.014, 24), new THREE.MeshStandardMaterial({ color: 0xfbfaf6, roughness: 0.2 }), MAX_SETTINGS),
    napkin: instanced(new THREE.BoxGeometry(ft(0.16), 0.025, ft(0.34)), new THREE.MeshStandardMaterial({ color: 0xf2eee4, roughness: 0.95 }), MAX_SETTINGS),
    glass: instanced(new THREE.CylinderGeometry(0.035, 0.025, 0.16, 10), new THREE.MeshStandardMaterial({ color: 0xdfe8ec, roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.55 }), MAX_SETTINGS * 2),
    flowers: instanced(new THREE.SphereGeometry(ft(0.6), 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 1.4, 1), new THREE.MeshStandardMaterial({ color: 0xf0d8d4, roughness: 1 }), MAX_CENTRES),
    greens: instanced(new THREE.CylinderGeometry(ft(0.75), ft(0.8), 0.05, 16), new THREE.MeshStandardMaterial({ color: 0x3a5a2e, roughness: 1 }), MAX_CENTRES),
    candle: instanced(new THREE.CylinderGeometry(0.035, 0.035, 0.22, 10), new THREE.MeshStandardMaterial({ color: 0xf4ecd8, roughness: 0.7 }), MAX_CENTRES * 3),
    flame: instanced(new THREE.SphereGeometry(0.018, 6, 4).scale(1, 1.8, 1), new THREE.MeshStandardMaterial({ color: 0x331100, emissive: 0xffb050, emissiveIntensity: 3 }), MAX_CENTRES * 3),
  };
  const put = (mesh, x, y, z, yaw = 0) => {
    if (mesh.count >= mesh.instanceMatrix.count) return;
    dummy.position.set(x, y, z);
    dummy.rotation.set(0, yaw, 0);
    dummy.updateMatrix();
    mesh.setMatrixAt(mesh.count, dummy.matrix);
    mesh.count += 1;
  };

  // --- pools ----------------------------------------------------------------
  const tables = [];
  const linens = [];
  const chairs = [];

  function table(index) {
    while (tables.length <= index) {
      const node = new THREE.Group();
      const top = new THREE.Mesh(tableGeometry, tableMaterial);
      top.position.y = ft(OBJECTS.round.height);
      top.castShadow = true;
      top.receiveShadow = true;
      const leg = new THREE.Mesh(legGeometry, [legMaterial]);
      leg.castShadow = true;
      node.add(top, leg);
      node.visible = false;
      group.add(node);
      tables.push(node);
    }
    return tables[index];
  }

  const clothGeometry = keep(new THREE.CylinderGeometry(ft(OBJECTS.round.radius + 0.05), ft(OBJECTS.round.radius + 0.05), 0.012, 24));
  function linen(index) {
    while (linens.length <= index) {
      const skirt = new THREE.Mesh(linenGeometry, linenMaterials.get('ivory'));
      skirt.castShadow = true;
      skirt.receiveShadow = true;
      skirt.visible = false;
      // The cloth over the top, in the same linen as the skirt.
      const cloth = new THREE.Mesh(clothGeometry, skirt.material);
      cloth.position.y = topY - ft(OBJECTS.round.height - 0.6) + 0.006;
      cloth.receiveShadow = true;
      skirt.add(cloth);
      group.add(skirt);
      linens.push(skirt);
    }
    return linens[index];
  }

  function chair(index) {
    while (chairs.length <= index) {
      const node = new THREE.Group();
      const body = new THREE.Mesh(chairGeometry, [chairFrameMaterial, chairMaterial]);
      body.castShadow = true;
      node.add(body);
      node.visible = false;
      group.add(node);
      chairs.push(node);
    }
    return chairs[index];
  }

  /** Rebuilds positions from the setup state. Called only when it changes. */
  function sync() {
    let tableIndex = 0;
    let linenIndex = 0;
    let chairIndex = 0;
    for (const mesh of Object.values(dressing)) mesh.count = 0;

    for (const entry of setup.tables) {
      const anchor = setup.anchorAt(entry.key);
      if (!anchor) continue;

      const node = table(tableIndex);
      node.position.set(anchor.x, 0, anchor.z);
      node.visible = true;
      tableIndex += 1;

      if (entry.cover) {
        const skirt = linen(linenIndex);
        skirt.material =
          linenMaterials.get(entry.cover.variant) ?? linenMaterials.get('ivory');
        skirt.children[0].material = skirt.material;
        skirt.position.set(anchor.x, ft(OBJECTS.round.height - 0.6), anchor.z);
        skirt.visible = true;
        linenIndex += 1;

        // The centrepiece: greens, a low dome of flowers, three candles.
        const y = topY + 0.012;
        put(dressing.greens, anchor.x, y + 0.025, anchor.z);
        put(dressing.flowers, anchor.x, y + 0.03, anchor.z);
        for (let c = 0; c < 3; c += 1) {
          const a = (c / 3) * Math.PI * 2 + 0.4;
          const cx = anchor.x + Math.cos(a) * ft(1.3);
          const cz = anchor.z + Math.sin(a) * ft(1.3);
          put(dressing.candle, cx, y + 0.11, cz);
          put(dressing.flame, cx, y + 0.25, cz);
        }
        // A place at every chair.
        for (const seat of setup.seatsFor(anchor, entry.seats, SEAT_RADIUS)) {
          const dx = seat.x - anchor.x;
          const dz = seat.z - anchor.z;
          const len = Math.hypot(dx, dz) || 1;
          const reach = ft(OBJECTS.round.radius) - ft(0.75);
          const px = anchor.x + (dx / len) * reach;
          const pz = anchor.z + (dz / len) * reach;
          const yaw = Math.atan2(dx, dz);
          put(dressing.charger, px, y + 0.004, pz);
          put(dressing.plate, px, y + 0.015, pz);
          put(dressing.napkin, px, y + 0.035, pz, yaw);
          // Glasses above the plate, to the right as you sit.
          const gx = px - (dx / len) * ft(0.7) + (dz / len) * ft(0.35);
          const gz = pz - (dz / len) * ft(0.7) - (dx / len) * ft(0.35);
          put(dressing.glass, gx, y + 0.08, gz);
          put(dressing.glass, gx + (dz / len) * ft(0.3), y + 0.08, gz - (dx / len) * ft(0.3));
        }
      }

      for (const seat of setup.seatsFor(anchor, entry.seats, SEAT_RADIUS)) {
        const node2 = chair(chairIndex);
        node2.position.set(seat.x, 0, seat.z);
        // Backs out, seats facing the table.
        node2.rotation.y = -seat.angle + Math.PI / 2;
        node2.visible = true;
        chairIndex += 1;
      }
    }

    for (const mesh of Object.values(dressing)) mesh.instanceMatrix.needsUpdate = true;
    for (let i = tableIndex; i < tables.length; i += 1) tables[i].visible = false;
    for (let i = linenIndex; i < linens.length; i += 1) linens[i].visible = false;
    for (let i = chairIndex; i < chairs.length; i += 1) chairs[i].visible = false;
  }

  sync();
  setup.onChange(sync);

  return {
    object3D: group,
    sync,

    /**
     * Shows the room's grid and, if there is one, the anchor about to be used.
     *
     * `bad` is for an anchor on the dance floor: legal, snappable, and the one
     * the staging notes told you not to use. The ring goes red rather than
     * refusing, because the game's job is to let you make the mistake and then
     * mark it.
     */
    setGuides({ visible, target = null, bad = false, anchors = false }) {
      // The rings are the sheet in your head. They are drawn only when
      // somebody upstream says both of the things that have to be true.
      anchorGroup.visible = visible && anchors;
      ghost.visible = visible && Boolean(target);
      if (!target) return;
      ghost.position.set(target.x, 0.02, target.z);
      ghostMaterial.color.setHex(bad ? GHOST_BAD_COLOUR : GHOST_COLOUR);
    },

    dispose() {
      for (const thing of disposables) thing.dispose?.();
      group.removeFromParent();
    },
  };
}
